// public/js/modules/sessionLog.js
// Captures all console output and unhandled errors from page load in a rolling buffer.

const MAX_LOG_ENTRIES = 1000;
const MAX_LOG_CHARS = 200000;
const MAX_ARG_LENGTH = 5000;

const entries = [];
let totalChars = 0;
const originals = {};

function truncate(str) {
    if (typeof str === 'string' && str.length > MAX_ARG_LENGTH) {
        return str.slice(0, MAX_ARG_LENGTH) + ` ...[truncated ${str.length - MAX_ARG_LENGTH} more chars]`;
    }
    return str;
}

function getCircularReplacer() {
    const seen = new WeakSet();
    return function (key, value) {
        if (value === undefined) return undefined;
        if (typeof value === 'bigint') return `${value}n`;
        if (value instanceof Error) return `${value.name}: ${value.message}`;
        if (typeof window !== 'undefined' && typeof Node !== 'undefined' && value instanceof Node) {
            let desc = value.tagName ? value.tagName.toLowerCase() : 'node';
            if (value.id) desc += `#${value.id}`;
            const className = typeof value.className === 'string' ? value.className : '';
            const classes = className.split(/\s+/).filter(Boolean);
            if (classes.length) desc += `.${classes.join('.')}`;
            return `[${desc}]`;
        }
        if (value !== null && typeof value === 'object') {
            if (seen.has(value)) return '[Circular]';
            seen.add(value);
        }
        return value;
    };
}

function formatArg(arg) {
    if (arg === null) return 'null';
    if (arg === undefined) return 'undefined';

    const type = typeof arg;
    if (type === 'string') return truncate(arg);
    if (type === 'number' || type === 'boolean') return String(arg);
    if (type === 'symbol') return arg.toString();
    if (type === 'bigint') return `${arg}n`;
    if (type === 'function') return `[Function: ${arg.name || 'anonymous'}]`;

    if (arg instanceof Error) {
        const stack = arg.stack ? `\n${arg.stack}` : '';
        return truncate(`${arg.name}: ${arg.message}${stack}`);
    }
    if (arg instanceof Date) return arg.toISOString();

    if (Array.isArray(arg)) {
        if (arg.length > 10) {
            return `[Array(${arg.length})]`;
        }
        return `[${arg.map(formatArg).join(', ')}]`;
    }

    if (type === 'object') {
        try {
            const keys = Object.keys(arg);
            if (keys.length > 10) {
                return `[Object(${keys.length})]`;
            }
            const summary = keys.slice(0, 5).map(k => `${k}:${formatArg(arg[k])}`).join(',');
            const suffix = keys.length > 5 ? `,...${keys.length - 5} more` : '';
            return `{${summary}${suffix}}`;
        } catch (e) {
            return '[object]';
        }
    }

    return String(arg);
}

function record(level, args) {
    const formattedArgs = Array.from(args).map(formatArg).join(' ');
    const firstArg = args[0];

    let line;
    if (typeof firstArg === 'string' && /^\[[A-Z][A-Za-z0-9_]*\]/.test(firstArg.trimStart())) {
        line = formattedArgs;
    } else {
        line = `[${level.toUpperCase()}] ${formattedArgs}`;
    }

    const timestamp = new Date().toISOString();
    const entry = `${timestamp} ${line}`;

    entries.push(entry);
    totalChars += entry.length + 1;

    while ((entries.length > MAX_LOG_ENTRIES || totalChars > MAX_LOG_CHARS) && entries.length > 0) {
        const removed = entries.shift();
        totalChars -= removed.length + 1;
    }
}

function patchConsole() {
    if (typeof console === 'undefined') return;
    const methods = ['log', 'info', 'warn', 'error', 'debug'];
    methods.forEach((method) => {
        if (typeof console[method] !== 'function') return;
        originals[method] = console[method];
        console[method] = function (...args) {
            record(method, args);
            try {
                originals[method].apply(console, args);
            } catch (e) {
                // Do not call console here to avoid recursion.
            }
        };
    });
}

function patchErrorHandlers() {
    if (typeof window === 'undefined') return;

    window.addEventListener('error', (event) => {
        const { message, filename, lineno, colno, error } = event;
        const err = error instanceof Error ? error : new Error(String(message || 'Unknown error'));
        record('error', [`[WINDOWERROR] ${message} at ${filename || '?'}:${lineno || 0}:${colno || 0}`, err]);
    });

    window.addEventListener('unhandledrejection', (event) => {
        record('error', [`[UNHANDLEDREJECTION] ${event.reason}`]);
    });
}

if (typeof window !== 'undefined') {
    patchConsole();
    patchErrorHandlers();
}

export function getLogText() {
    return entries.join('\n');
}

export function getLogBlob() {
    const text = getLogText();
    return new Blob(['\uFEFF' + text], { type: 'text/plain;charset=utf-8' });
}

export function getLogPreview(lineCount = 100) {
    return entries.slice(-lineCount).join('\n');
}

export function getSessionInfo() {
    const store = (typeof window !== 'undefined' && window.useAppStore) ? window.useAppStore.getState() : null;

    const info = {
        timestamp: new Date().toISOString(),
        userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
        platform: typeof navigator !== 'undefined' ? navigator.platform : '',
        language: typeof navigator !== 'undefined' ? navigator.language : '',
        online: typeof navigator !== 'undefined' ? navigator.onLine : null,
        url: typeof window !== 'undefined' ? window.location.href : '',
        pathname: typeof window !== 'undefined' ? window.location.pathname : '',
        search: typeof window !== 'undefined' ? window.location.search : '',
        hash: typeof window !== 'undefined' ? window.location.hash : '',
        referrer: typeof document !== 'undefined' ? document.referrer : '',
        title: typeof document !== 'undefined' ? document.title : '',
        visibilityState: typeof document !== 'undefined' ? document.visibilityState : '',
        bodyClasses: (typeof document !== 'undefined' && document.body) ? document.body.className : '',
        screen: {
            width: typeof screen !== 'undefined' ? screen.width : null,
            height: typeof screen !== 'undefined' ? screen.height : null,
            availWidth: typeof screen !== 'undefined' ? screen.availWidth : null,
            availHeight: typeof screen !== 'undefined' ? screen.availHeight : null,
            colorDepth: typeof screen !== 'undefined' ? screen.colorDepth : null,
        },
        viewport: {
            innerWidth: typeof window !== 'undefined' ? window.innerWidth : null,
            innerHeight: typeof window !== 'undefined' ? window.innerHeight : null,
            devicePixelRatio: typeof window !== 'undefined' ? window.devicePixelRatio : null,
        },
    };

    if (store) {
        info.currentLoadedSongId = store.currentLoadedSongId ?? null;
        info.currentArtistId = store.currentArtistId ?? null;
        info.currentShowId = store.currentShowId ?? null;
        info.isLiveShow = store.isLiveShow ?? false;
        info.isGuest = store.isGuest ?? false;
        info.guestName = store.guestName ?? null;
    }

    return info;
}

export function clearSessionLog() {
    entries.length = 0;
    totalChars = 0;
}
